'use client';
import cn from 'classnames';
import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { ButtonType } from '../button/button';
import Button from '../button/button';
import styles from './modal.module.scss';

interface ModalProps {
  isOpen: boolean;
  hasOverlay?: boolean;
  title?: string;
  price?: string;
  description: string;
  firstButtonText: string;
  firstButtonType: ButtonType;
  secondButtonText?: string;
  secondButtonType?: ButtonType;
  isRectangular?: boolean;
  isButtonContentSize?: boolean;
  isFullSize?: boolean;
  testId?: string;
  onFirstButtonClick?: () => void;
  onSecondButtonClick?: () => void;
  onOverlayClick?: () => void;
}

export default function Modal({
  isOpen,
  hasOverlay = true,
  title,
  price,
  description,
  firstButtonText,
  firstButtonType,
  secondButtonText,
  secondButtonType,
  isRectangular = true,
  isButtonContentSize,
  isFullSize,
  testId,
  onFirstButtonClick,
  onSecondButtonClick,
  onOverlayClick,
}: Readonly<ModalProps>) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(isOpen);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    const modalElement = dialogRef.current;
    if (!modalElement) {
      return;
    }

    if (isModalOpen) {
      hasOverlay ? modalElement.showModal() : modalElement.show();
    } else {
      modalElement.close();
    }
  }, [isModalOpen, hasOverlay]);

  const dialogClickHandler = (e: MouseEvent) => {
    const targetElement = e.target as HTMLDialogElement;
    if (targetElement?.nodeName !== 'DIALOG' && !hasOverlay && !onOverlayClick) {
      return;
    }

    const rect = targetElement.getBoundingClientRect();

    const clickedInDialog =
      rect.top <= e.clientY &&
      e.clientY <= rect.top + rect.height &&
      rect.left <= e.clientX &&
      e.clientX <= rect.left + rect.width;

    if (!clickedInDialog && onOverlayClick) {
      onOverlayClick();
    }
  };

  const firstButtonClickHandler = () => {
    onFirstButtonClick && onFirstButtonClick();
    setIsModalOpen(false);
  };
  const secondButtonClickHandler = () => {
    onSecondButtonClick && onSecondButtonClick();
    setIsModalOpen(false);
  };

  return (
    <dialog className={styles.dialog} ref={dialogRef} onClick={dialogClickHandler}>
      <div
        className={cn(styles.contentContainer, {
          [styles.hasOverlay]: hasOverlay,
        })}
        data-test-id={testId}
      >
        <div className={styles.content}>
          <div className={styles.header}>
            {title && <span className={styles.title}>{title}</span>}
            {price && (
              <span className={styles.price} data-test-id="modal-price">
                {price}
              </span>
            )}
          </div>
          <div className={styles.description}>{description}</div>
          <div
            className={cn(styles.buttonsContainer, {
              [styles.contentSize]: isButtonContentSize,
            })}
          >
            <Button
              onClick={firstButtonClickHandler}
              type={firstButtonType}
              isRectangular={isRectangular}
              testId="modal-primary-btn"
              isFullSize={isFullSize}
            >
              {firstButtonText}
            </Button>
            {secondButtonText && (
              <Button
                onClick={secondButtonClickHandler}
                type={secondButtonType}
                isRectangular={isRectangular}
                testId="modal-secondary-btn"
              >
                {secondButtonText}
              </Button>
            )}
          </div>
        </div>
      </div>
    </dialog>
  );
}
